import React, { useEffect, useState } from 'react';
import './RazorpayPayment.css';
import { apiRequest, parseResponse, getLoggedInUserId } from '../utils/api';

const RazorpayPayment = ({ amount, bookingData, selectedCard, paymentMethod, onSuccess, onFailure, onCancel, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  // Debug: Log environment variable on component mount (remove in production)
  useEffect(() => {
    console.log('Razorpay Key from env:', process.env.REACT_APP_RAZORPAY_KEY_ID ? 'Found' : 'Missing');
  }, []);

  useEffect(() => {
    // Load Razorpay checkout script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      document.querySelectorAll('.razorpay-container, .razorpay-backdrop').forEach((node) => node.remove());
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      const guestData = JSON.parse(localStorage.getItem('guestData') || '{}');
      const userData = guestData?.userData || {};
      const actualUserData = userData?.data || userData;
      const bookingId = bookingData?.bookingId || bookingData?.id || null;

      if (!bookingId) {
        throw new Error('Booking ID not found. Cannot create payment order.');
      }

      const orderPayload = {
        amount: Math.round(amount),
        currency: 'INR',
        bookingId
      };

      const orderResponse = await apiRequest('/payments/create-order', {
        method: 'POST',
        body: JSON.stringify(orderPayload)
      });

      if (!orderResponse.ok) {
        const errorText = await orderResponse.text();
        throw new Error(errorText || 'Failed to create payment order');
      }

      const orderData = await parseResponse(orderResponse);
      if (!orderResponse.ok || orderData?.status === false) {
        throw new Error(orderData?.message || 'Failed to create payment order');
      }
      const order = orderData?.data || orderData;
      
      if (!order || !order.id) {
        throw new Error('Invalid order response from server');
      }

      // Get Razorpay key from order response or environment variable
      // Try multiple possible field names from backend
      const razorpayKey = order.key || 
                         order.razorpayKey || 
                         order.razorpay_key_id ||
                         order.key_id ||
                         process.env.REACT_APP_RAZORPAY_KEY_ID ||
                         'rzp_test_SDiRM8tiLqJl67'; // Fallback to your key
      
      // Debug logging
      console.log('Razorpay Key Debug:', {
        'order.key': order.key,
        'order.razorpayKey': order.razorpayKey,
        'order.razorpay_key_id': order.razorpay_key_id,
        'order.key_id': order.key_id,
        'process.env.REACT_APP_RAZORPAY_KEY_ID': process.env.REACT_APP_RAZORPAY_KEY_ID,
        'selectedKey': razorpayKey ? 'Found' : 'Missing',
        'order object keys': Object.keys(order)
      });
      
      if (!razorpayKey) {
        throw new Error('Razorpay authentication key is missing. Please configure REACT_APP_RAZORPAY_KEY_ID in your .env file or ensure the backend returns the key in the order response.');
      }

      const backendAmount = Number(order.amount);
      const razorpayAmount = backendAmount > 0 ? backendAmount : Math.round(Number(amount) * 100);
      const preferredMethod = selectedCard || paymentMethod === 'card'
        ? 'card'
        : (paymentMethod === 'upi' ? 'upi' : undefined);
      
      const options = {
        key: razorpayKey, // Your Razorpay Key ID
        amount: razorpayAmount, // Amount in paise (60100 for ₹601.00)
        currency: order.currency || 'INR',
        name: 'Moviq',
        description: `Booking Payment - ${bookingData?.startLocation || 'Trip'}`,
        order_id: order.id,
        'prefill.card_token': selectedCard?.tokenId || undefined, // Token ID for saved card
        handler: async function (response) {
          // Payment successful
          try {
            // Extract booking_id from bookingData
            // bookingData should have bookingId at top level (set in App.js)
            const bookingId = bookingData?.bookingId || 
                             bookingData?.id || 
                             null;
            
            if (!bookingId) {
              console.error('Booking ID not found in bookingData:', bookingData);
              throw new Error('Booking ID not found. Cannot verify payment.');
            }
            
            // Verify payment on backend
            const verifyResponse = await apiRequest('/payments/verify', {
              method: 'POST',
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                booking_id: bookingId,
                saveCard: paymentMethod === 'card' && saveCard,
                userId: getLoggedInUserId()
              })
            });

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            const verifyData = await parseResponse(verifyResponse);
            
            if (verifyData?.status || verifyData?.success) {
              onSuccess({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                ...verifyData
              });
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (err) {
            console.error('Payment verification error:', err);
            onFailure(err.message || 'Payment verification failed');
          }
        },
        prefill: {
          name: actualUserData?.name || guestData?.fullName || '',
          email: actualUserData?.email || '',
          contact: actualUserData?.mobileNumber || guestData?.mobileNumber || '',
          method: preferredMethod
        },
        theme: {
          color: '#764CA2'
        },
        modal: {
          ondismiss: function() {
            onCancel();
          }
        },
        save: paymentMethod === 'card' && saveCard ? 1 : 0,
        save_card: paymentMethod === 'card' && saveCard ? 1 : 0
      };

      // Open Razorpay checkout
      if (window.Razorpay) {
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        throw new Error('Razorpay SDK not loaded. Please refresh the page.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to initiate payment. Please try again.');
      onFailure(err.message || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="razorpay-payment-screen">
      <div className="razorpay-payment-content">
        {/* Header */}
        <header className="razorpay-payment-header">
          <button className="back-button" onClick={onBack} aria-label="Go back">
            <img
              src={process.env.PUBLIC_URL + '/images/back_button.svg'}
              alt="Back"
            />
          </button>
          <h1 className="razorpay-payment-title">Payment</h1>
        </header>

        {/* Payment Details */}
        <div className="payment-details-card">
          <div className="payment-amount-section">
            <span className="payment-amount-label">Amount to Pay</span>
            <span className="payment-amount-value">₹ {amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>

          {bookingData && (
            <div className="payment-booking-info">
              <div className="booking-info-item">
                <span className="booking-info-label">From:</span>
                <span className="booking-info-value">{bookingData.startLocation || 'N/A'}</span>
              </div>
              <div className="booking-info-item">
                <span className="booking-info-label">To:</span>
                <span className="booking-info-value">{bookingData.endLocation || 'N/A'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Selected Card Info */}
        {selectedCard && (
          <div className="selected-card-info">
            <div className="card-icon-small">
              <span className="card-type-badge-small">{selectedCard.cardType || selectedCard.brand || 'CARD'}</span>
            </div>
            <div className="card-info-small">
              <span className="card-number-small">**** **** **** {selectedCard.last4 || selectedCard.lastFour || '****'}</span>
              <span className="card-name-small">{selectedCard.name || 'Card Holder'}</span>
            </div>
          </div>
        )}

        {paymentMethod === 'card' && !selectedCard && (
          <div className="save-card-option">
            <label className="save-card-checkbox">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
              />
              <span className="save-card-label">Save card for future payments</span>
            </label>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="payment-error">
            <p>{error}</p>
          </div>
        )}

        {/* Payment Button */}
        <button
          className="razorpay-pay-button"
          onClick={handlePayment}
          disabled={loading}
        >
          {loading ? 'Processing...' : `Pay ₹ ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        </button>

        {/* Security Info */}
        <div className="payment-security-info">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p>Your payment is secured by Razorpay</p>
        </div>
      </div>
    </div>
  );
};

export default RazorpayPayment;
